import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { closeModalAction } from '../../../actions/modal';
import { useDispatch, useSelector } from 'react-redux';
import Form from '../../../common/Form';
import { useForm, useFormContext } from 'react-hook-form';
import MultipleEmails from '../../../common/Form/MultipleEmail';
import DateRange from '../../../common/Form/DateRange';
import { requestReport, getReportColumns } from '../../../actions/utils';
import moment from 'moment';
import { ReportsName } from '../../../utils/utils';
import { actualValueForDropdown, debounce, GetQueryParam, ScogoID } from '../../../utils/common';
import { canManageOtherOrgs } from '../../../utils/access';
import ButtonScogoPrimary from '../../../common/Buttons/ButtonScogoPrimary';
import ButtonScogoClosedOutlined from '../../../common/Buttons/ButtonScogoClosedOutlined';
import { useLocation } from 'react-router-dom';
import { ticket as ticketPath, payments as paymentsPath, inventory as inventoryPath } from '../../../router/roleWisePageRoute';
import Select from '../../../common/Form/Select';
import { getOrgList, resetOrgDropdownList } from '../../../actions/org';
import { downloadCustomerSignoffReport } from '../../../actions/service_request';
import { getProjectListData } from '../../../actions/project';
import { CLEAR_PROJECT_DROPDOWN_LIST_DATA } from '../../../types/project';
import { RegionsType, TicketAgentRole } from '../../Ticket/TicketController';
import { assets, InventoryLocationType, sites, spareAssets, warehouses } from '../../Inventory/InventoryController';

const formName = 'requestStatement';
const reportName = 'WALLET_STATEMENT';

// Reports that let the user pick the projects to report on. `showProjectFilter` overrides this per usage,
// eg. a single ticket report is already scoped by its ticket id and does not need the dropdown.
const projectFilterReports = [ReportsName.TICKET, ReportsName.FEASIBILITY, ReportsName.EXPENSE, ReportsName.RMA, ReportsName.PAYMENTS, ReportsName.PROJECT_BUDGET_REPORT, ReportsName.TICKET_TRANSACTIONS, ReportsName.PAV];

export default function RequestStatement({ report, showDateRange = false, filterData = {}, selectedProjects = [], projectRequired = true, showProjectFilter, applyPageFilters = true }) {
    const dispatch = useDispatch();
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);
    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);
    const defaultValues = { emails: newLoggedUserData.user.email ? [newLoggedUserData.user.email] : [] };
    const reportColumns = useSelector((state) => state.utils.reportColumns);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [editableLabels, setEditableLabels] = useState({});
    const serviceRequestFilterPayload = useSelector((state) => state.serviceRequest.serviceRequestFilterPayload);
    const formRef = useRef(null);
    const path = useLocation().pathname;
    const serviceRequestFilterList = useSelector((state) => state.serviceRequest.serviceRequestFilterList);
    const paymentFilterPayload = useSelector((state) => state.serviceRequest.paymentFilterPayload);
    const poFilterPayload = useSelector((state) => state.payments.filterPayload);
    const locationFilterData = useSelector((state) => state.inventory.locationFilterData);
    const assetsFilterData = useSelector((state) => state.inventory.assetsFilterData);
    const tab = GetQueryParam('tab');

    const appliedFilters = useMemo(() => {
        let f = { ...filterData };
        if (!applyPageFilters) return f;
        if (path === ticketPath.path) {
            Object.entries(serviceRequestFilterPayload.payload).forEach(([key, value]) => {
                if (value === undefined || value === null || value === '') return;
                if (key === 'project_id') {
                    value = value.split(',');
                    f.projects = value;
                } else if (key === 'execution_dates_range') {
                    value = value.split(',');
                    f.execution_date_from = value?.[0];
                    f.execution_date_to = value?.[1];
                } else if (key === 'milestone_type') {
                    value = value.split(',');
                    f.milestone_type = value
                } else if (key === 'org_id') {
                    value = value.split(',');
                    f.orgs = value
                } else if (key === 'end_user_id') {
                    value = value.split(',');
                    f.end_users = value
                } else if (key === 'status') {
                    value = value.split(',');
                    f.status = value
                } else if (key === 'milestone_status') {
                    value = value.split(',');
                    f.milestone_status = value
                } else if (key === 'location_id') {
                    value = value.split(',');
                    f.location_id = value
                } else if (key === 'invoice_generated') {
                    f.invoice_raised = value
                } else if (key === 'creation_dates_range') {
                    value = value.split(',');
                    f.created_at_from = value?.[0];
                    f.created_at_to = value?.[1];
                } else if (key === 'closed_dates_range') {
                    value = value.split(',');
                    f.site_completed_on_from = value?.[0];
                    f.site_completed_on_to = value?.[1];
                } else if (key === 'approval_dates_range') {
                    value = value.split(',');
                    f.signoff_approved_date_from = value?.[0];
                    f.signoff_approved_date_to = value?.[1];
                } else if (key === TicketAgentRole.CLUSTER) {
                    f.clusters = value;
                } else if (key === TicketAgentRole.PSP) {
                    f.psps = value;
                } else if (key === TicketAgentRole.SUPERVISOR) {
                    f.supervisors = value;
                } else if (key === TicketAgentRole.SP) {
                    f.partners = value;
                } else if (key === TicketAgentRole.FE) {
                    f.engineers = value;
                } else if (key === TicketAgentRole.REMOTE_SUPPORT) {
                    f.agents = value;
                } else if (key === RegionsType.STATE) {
                    f.states = value;
                } else if (key === RegionsType.CITY) {
                    f.cities = value;
                } else if (key === RegionsType.DISTRICT) {
                    f.districts = value;
                } else if (key === RegionsType.LOCALITY) {
                    f.localities = value;
                } else if (key === RegionsType.REGION_TIER) {
                    f.city_tiers = value;
                } else if (key === 'customer_approval_received') {
                    f.customer_approval_received = value
                }

                else if (key === 'external_ids') {
                    f.external_ids = value.split(' ');
                }
                else if (key === 'external_ticket_ids') {
                    f.external_ticket_ids = value.split(' ');
                }
                else if (key === 'pincode') {
                    f.pincodes = value.split(' ');
                }
                else if (key === 'serial_number') {
                    f.asset_serial_numbers = value.split(' ');
                }
                else if (key === 'ticket_number') {
                    f.ticket_numbers = value.split(' ');
                } else if (key === 'is_rma_pending') {
                    f.is_rma_pending = value;
                } else if (key === 'rma_consumption_type') {
                    f.rma_consumption_type = value
                } else if (key === 'sign_off_approve_lock' && value === true) {
                    f.signoff_approve_locked = value;
                } else if (key === 'sign_off_lock' && value === true) {
                    f.signoff_request_locked = value;
                }
                // base_payment_due_date_from
                // base_payment_due_date_to
                // txn_date_from
                // txn_date_to
                // base_payment_status
                // requested_amount_approval_pending_min
                // requested_amount_approval_pending_max

            });
        };

        if (tab === 'po' && path === paymentsPath.path) {
            Object.entries(poFilterPayload).forEach(([key, value]) => {
                if (key === 'status') {
                    value = value.split(',');
                    f.po_status = value;
                }

                if (key === 'location_id') {
                    value = value.split(',');
                    f.location_id = value;
                }

                if (key === 'org_id') {
                    value = value.split(',');
                    f.orgs = value;
                }

                if (key === 'vendor_id') {
                    value = value.split(',');
                    f.vendors = value;
                }

                if (key === 'next_renewable_date_from') {
                    f.next_renewal_date_from = value;
                }

                if (key === 'next_renewable_date_to') {
                    f.next_renewal_date_to = value;
                }

                if (key === 'created_at_from') {
                    f.created_at_from = value;
                }

                if (key === 'created_at_to') {
                    f.created_at_to = value;
                }

            });
        } else if (path === paymentsPath.path && paymentFilterPayload?.payload) {
            Object.entries(paymentFilterPayload.payload).forEach(([key, value]) => {
                if (key === "additional_amount_requested") {
                    f.additional_amount_requested = value;
                }

                if (!value) return;

                if (key === 'ticket_status') {
                    value = value.split(',');
                    f.status = value
                } else if (key === 'project_id') {
                    value = value.split(',');
                    f.projects = value;
                } else if (key === 'org_id') {
                    value = value.split(',');
                    f.orgs = value
                } else if (key === 'agent_id') {
                    value = value.split(',');
                    f.partners = value
                } else if (key === RegionsType.STATE) {
                    f.states = value;
                } else if (key === RegionsType.CITY) {
                    f.cities = value;
                } else if (key === 'closed_from_date') {
                    f.signoff_requested_date_from = value;
                } else if (key === 'closed_to_date') {
                    f.signoff_requested_date_to = value;
                } else if (key === 'due_date_from') {
                    f.base_payment_due_date_from = value;
                } else if (key === 'due_date_to') {
                    f.base_payment_due_date_to = value;
                } else if (key === 'status') {
                    value = value.split(',');
                    if (value?.length > 0) f.base_payment_status = value[0];
                } else if (key === 'signoff_approved_at_from_date') {
                    f.signoff_approved_date_from = value;
                } else if (key === 'signoff_approved_at_to_date') {
                    f.signoff_approved_date_to = value;
                } else if (key === 'requested_additional_pending_amount_min') {
                    f.requested_additional_pending_amount_min = value;
                } else if (key === 'requested_additional_pending_amount_max') {
                    f.requested_additional_pending_amount_max = value;
                }
            });
        };

        if (path === inventoryPath.path && [assets.path, spareAssets.path].includes(tab)) {
            const isScogoMember = newLoggedUserData.orgMemberships.find(({ id }) => id === ScogoID);
            if (!isScogoMember) f.orgs = [newLoggedUserData.org.id];

            if (assets.path === tab) f.is_spare = false;
            else f.is_spare = true;

            Object.entries(assetsFilterData?.payload).forEach(([key, value]) => {
                if (!value) return;

                if (key === 'org_id') {
                    value = value.split(',');
                    f.orgs = value
                } else if (key === 'asset_type_id') {
                    value = value.split(',');
                    f.asset_type_ids = value
                } else if (key === 'status') {
                    value = value.split(',');
                    f.asset_status = value
                } else if (key === 'working_condition') {
                    value = value.split(',');
                    f.asset_working_condition = value
                } else if (key === 'created_at_range') {
                    value = value.split(',');
                    f.asset_added_on_from = value?.[0];
                    f.asset_added_on_to = value?.[1];
                } else if (key === 'location_id') {
                    value = value.split(',');
                    f.location_id = value
                }
            });

        } else if (path === inventoryPath.path) {
            const isScogoMember = newLoggedUserData.orgMemberships.find(({ id }) => id === ScogoID);
            if (!isScogoMember) f.orgs = [newLoggedUserData.org.id];

            if (warehouses.path === tab) f.is_warehouse = true;
            else f.is_warehouse = false;

            Object.entries(locationFilterData?.payload).forEach(([key, value]) => {
                if (!value) return;

                if (key === 'org_id') {
                    value = value.split(',');
                    f.orgs = value
                } else if (key === 'end_user_id') {
                    value = value.split(',');
                    f.end_users = value
                } else if (key === 'asset_type_id') {
                    value = value.split(',');
                    f.asset_type_ids = value
                } else if (key === RegionsType.STATE) {
                    f.states = value;
                } else if (key === RegionsType.CITY) {
                    f.cities = value;
                }
            });

        };
        return f;
    }, [filterData, applyPageFilters, serviceRequestFilterPayload?.payload, path, paymentFilterPayload?.payload, tab, newLoggedUserData.orgMemberships, newLoggedUserData.org.id, locationFilterData?.payload, assetsFilterData?.payload, poFilterPayload]);

    const memoizedSelectedProjects = useMemo(() => {
        if (path === ticketPath.path && appliedFilters?.projects?.length > 0 && serviceRequestFilterList?.projects?.length > 0) {
            let filterProject = serviceRequestFilterList?.projects?.filter(({ value }) => appliedFilters?.projects.includes(value));
            if (filterProject.length > 0) {
                return filterProject.map((elem) => ({ ...elem, key: elem.value }))
            }
        }

        if (tab === 'po' && path === paymentsPath.path) {

        } else if (path === paymentsPath.path && appliedFilters?.projects?.length > 0 && serviceRequestFilterList?.projects?.length > 0) {
            let filterProject = serviceRequestFilterList?.projects?.filter(({ value }) => appliedFilters?.projects.includes(value));
            if (filterProject.length > 0) {
                return filterProject.map((elem) => ({ ...elem, key: elem.value }))
            }
        } else if (report === ReportsName.PROJECT_BUDGET_REPORT && selectedProjects.length > 0) {
            return selectedProjects;
        }
        return [];
    }, [appliedFilters?.projects, serviceRequestFilterList, path, tab, report, selectedProjects]);

    const memoizedSelectedOrgs = useMemo(() => {
        if (path === ticketPath.path && appliedFilters?.orgs?.length > 0 && serviceRequestFilterList?.org?.length > 0) {
            let filterOrgs = serviceRequestFilterList?.org?.filter(({ value }) => appliedFilters?.orgs.includes(value));
            if (filterOrgs.length > 0) {
                return filterOrgs.map((elem) => ({ ...elem, key: elem.value }))
            }
        }
        if (tab === 'po' && path === paymentsPath.path) {

        } else if (path === paymentsPath.path && appliedFilters?.orgs?.length > 0 && serviceRequestFilterList?.org?.length > 0) {
            let filterOrgs = serviceRequestFilterList?.org?.filter(({ value }) => appliedFilters?.orgs.includes(value));
            if (filterOrgs.length > 0) {
                return filterOrgs.map((elem) => ({ ...elem, key: elem.value }))
            }
        }
        return [];
    }, [appliedFilters?.orgs, serviceRequestFilterList, path, tab]);


    const columns = useMemo(() => {
        return reportColumns[report] ? reportColumns[report] : [];
    }, [reportColumns, report]);

    useEffect(() => {
        dispatch(getReportColumns({ report, formName: reportName }));
    }, [dispatch, report]);

    useEffect(() => {
        if (columns.length > 0) {
            setSelectedColumns(columns.map(col => col.key));
            const labelsObj = {};
            columns.forEach(col => {
                labelsObj[col.key] = col.label;
            });
            setEditableLabels(labelsObj);
        }
    }, [columns]);

    const toggleColumnSelection = (key) => {
        setSelectedColumns(prev => {
            if (prev.includes(key)) {
                return prev.filter(k => k !== key);
            } else {
                return [...prev, key];
            }
        });
    };

    const handleLabelChange = (key, newLabel) => {
        setEditableLabels(prev => ({
            ...prev,
            [key]: newLabel
        }));
    };

    const manageOtherOrgs = canManageOtherOrgs(newLoggedUserData.actions);

    const submit = (formValues) => {
        let { duration, emails } = formValues;

        if (report === ReportsName.CUSTOMER_SIGNOFF) {
            dispatch(downloadCustomerSignoffReport({ payload: { ...filterData, emails } }))
            return;
        }

        const selectedColumnsData = columns
            .filter(col => selectedColumns.includes(col.key))
            .map(col => ({
                key: col.key,
                label: editableLabels[col.key] || col.label
            }));


        const updatedFilters = { ...appliedFilters };
        const projects = formValues?.projects?.map((elem) => actualValueForDropdown(elem));
        const orgs = formValues?.organization?.map((elem) => actualValueForDropdown(elem));

        if (projects) updatedFilters.projects = projects;
        if (orgs) updatedFilters.orgs = orgs;

        if (report === ReportsName.OFFLINE_AND_BANK_TRANSFER_PAYMENTS) {
            updatedFilters.txn_date_from = duration?.[0] ? moment(duration[0]).format('YYYY-MM-DD') : '';
            updatedFilters.txn_date_to = duration?.[1] ? moment(duration[1]).format('YYYY-MM-DD') : '';
        }
        if (report === ReportsName.TICKET_TRANSACTIONS) {
            updatedFilters.txn_date_from = duration?.[0] ? moment(duration[0]).format('YYYY-MM-DD') : '';
            updatedFilters.txn_date_to = duration?.[1] ? moment(duration[1]).format('YYYY-MM-DD') : '';
        }
        if (report === ReportsName.USER_ATTENDANCE) {
            updatedFilters.created_at_from = duration?.[0] ? moment(duration[0]).format('YYYY-MM-DD') : '';
            updatedFilters.created_at_to = duration?.[1] ? moment(duration[1]).format('YYYY-MM-DD') : '';
        }

        let payload = {
            emails: emails,
            report,
            filters: {
                columns: selectedColumnsData,
                ...updatedFilters
            }
        };

        switch (report) {
            case 'WALLET_STATEMENT':
                payload.filters = {
                    txn_date_from: duration?.[0] ? moment(duration[0]).format('YYYY-MM-DD') : '',
                    txn_date_to: duration?.[1] ? moment(duration[1]).format('YYYY-MM-DD') : '',
                    columns: selectedColumnsData,
                    ...filterData
                };
                break;
            default:
                break;
        }

        dispatch(requestReport({ payload, formName }));
    };

    const methods = useForm({ defaultValues, mode: 'all' });

    const isProjectRequired = Object.keys(appliedFilters).length === 0 && projectRequired;
    const isProjectFilterVisible = typeof showProjectFilter === 'boolean' ? showProjectFilter : projectFilterReports.includes(report);

    return (
        <div className='h-auto relative overflow-hidden max-h-[65vh]'>
            <div className='max-h-[54vh] flex-1 overflow-y-scroll'>
                <Form
                    methods={methods}
                    onSubmit={submit}
                    submitRef={formRef}
                >
                    <div className='w-full flex gap-2 px-2 py-2'>
                        {isProjectFilterVisible ? <RenderProject required={isProjectRequired} className='w-full' name='projects' label='Projects' memoizedSelectedProjects={memoizedSelectedProjects} /> : null}
                        {[ReportsName.RMA, ReportsName.TICKET_TRANSACTIONS].includes(report) && manageOtherOrgs ? <RenderCustomer memoizedSelectedOrgs={memoizedSelectedOrgs} isMulti className='w-full' name='organization' label='Organization' /> : null}
                    </div>

                    <div className='px-3 py-2'>
                        <div className='flex gap-2'>
                            <MultipleEmails className='w-full' name='emails' required label='Email To' />
                            {showDateRange && <DateRange className='w-full' label='Select Duration' name='duration' dateFormat='dd-MM-yyyy' placeholder={'Select Duration'} required showMonthDropdown showYearDropdown />}
                        </div>

                        {columns.length > 0 && (
                            <div>
                                <label className='block text-sm font-medium text-gray-700 mb-2'>
                                    Customize Report Columns
                                </label>
                                <div className='bg-gray-50 p-3 rounded-md border border-gray-200 max-h-60 overflow-y-auto'>
                                    <div className='space-y-2'>
                                        <div className='flex justify-between mb-2 pb-2 border-b border-gray-200'>
                                            <button
                                                type='button'
                                                className='text-xs text-blue-600 hover:text-blue-800'
                                                onClick={() => setSelectedColumns(columns.map(col => col.key))}
                                            >
                                                Select All
                                            </button>
                                            <button
                                                type='button'
                                                className='text-xs text-blue-600 hover:text-blue-800'
                                                onClick={() => setSelectedColumns([])}
                                            >
                                                Deselect All
                                            </button>
                                        </div>

                                        <div className='grid grid-cols-3 gap-3'>
                                            {columns.map((column) => (
                                                <div key={column.key} className='flex items-center space-x-2 p-2 hover:bg-gray-100 rounded-md transition-colors'>
                                                    <input
                                                        type='checkbox'
                                                        id={`column-${column.key}`}
                                                        checked={selectedColumns.includes(column.key)}
                                                        onChange={() => toggleColumnSelection(column.key)}
                                                        className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
                                                    />
                                                    <div className='flex-grow'>
                                                        <input
                                                            type='text'
                                                            value={editableLabels[column.key] || ''}
                                                            onChange={(e) => handleLabelChange(column.key, e.target.value)}
                                                            className='w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                                                            disabled={!selectedColumns.includes(column.key)}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <p className='mt-1 text-xs text-gray-500'>
                                    Select columns to include in your report and customize their labels if needed.
                                </p>
                            </div>
                        )}
                    </div>
                </Form>
            </div>
            <div className={`px-6 py-2 flex gap-2 justify-end mt-4 relative`}>
                <ButtonScogoPrimary loading={formButtonLoading[formName]} textOrComponent='Request' onClick={() => methods.handleSubmit(submit)()} />
                <ButtonScogoClosedOutlined loading={false} textOrComponent='Cancel' onClick={() => dispatch(closeModalAction())} />
            </div>
        </div>
    );
}





const RenderCustomer = ({ className, label, name, required = false, disabled, isMulti = true, onChange = () => { }, memoizedSelectedOrgs = [] }) => {

    const dispatch = useDispatch();
    const methods = useFormContext();
    const orgListDropdown = useSelector((state) => state.org.orgListDropdown);
    const isOrgListLoading = useSelector((state) => state.loading.isOrgListLoading);


    const getOrganisationList = ({ limit = 100, page = 1, query = '' }) => {
        dispatch(getOrgList({ limit, page, query, dropdown: true, type: 'CUSTOMER' }));
    };

    const debouncedOrgSearchSearch = useCallback(debounce(getOrganisationList), []);


    useEffect(() => {
        getOrganisationList({ limit: 100, page: 1, query: '' });
        return () => {
            dispatch(resetOrgDropdownList());
        }
    }, []);

    const memoizedOrgDropdown = useMemo(() => {
        let data = [];
        if (memoizedSelectedOrgs?.length > 0) {
            data = [...memoizedSelectedOrgs];
        }
        if (orgListDropdown?.length > 0) {
            data.push(...orgListDropdown)
        }
        return Array.from(new Map(data.map(item => [item.value, item])).values());
    }, [orgListDropdown, memoizedSelectedOrgs]);


    useEffect(() => {
        if (memoizedSelectedOrgs?.length > 0) {
            methods.setValue(name, memoizedSelectedOrgs);
        }
    }, [memoizedSelectedOrgs]);

    return <Select
        className={className}
        label={label}
        name={name}
        required={required}
        options={memoizedOrgDropdown}
        isLoading={isOrgListLoading}
        onInputChange={(event) => {
            if (event.length > 0) {
                debouncedOrgSearchSearch({ query: event })
            }
        }}
        onChange={(event) => {
            const orgId = actualValueForDropdown(event);
            if (orgId) {
                onChange(orgId)
            }
        }}
        readOnly={disabled}
        preSelectedIfSingleOption={true}
        isMulti={isMulti}
    />
}


const RenderProject = ({ name, label, onChange = () => { }, org_id, required = true, org_check_required = true, readOnly, className = '', memoizedSelectedProjects = [] }) => {
    const dispatch = useDispatch();
    const projectDropdownListData = useSelector((state) => state.project.projectDropdownListData);
    const isProjectDropDownListLoading = useSelector((state) => state.loading.isProjectDropDownListLoading);
    const methods = useFormContext();

    const getProjectList = ({ limit = 100, page = 1, query = '', org_id }) => {
        dispatch(getProjectListData({ limit, page, query, dropdown: true, payload: { org_id } }));
    };

    const debouncedProjectSearchSearch = useCallback(debounce(getProjectList), []);

    useEffect(() => {
        let payload = {
            limit: 100,
            page: 1,
        }
        if (org_check_required) payload.org_id = org_id;
        getProjectList(payload);
    }, [org_id]);


    useEffect(() => {
        return () => {
            dispatch({ type: CLEAR_PROJECT_DROPDOWN_LIST_DATA });
        }
    }, [dispatch]);

    const memoizedProjectList = useMemo(() => {
        let data = [];
        if (memoizedSelectedProjects?.length > 0) {
            data = [...memoizedSelectedProjects];
        };
        if (projectDropdownListData?.length > 0) {
            data.push(...projectDropdownListData);
        };
        return Array.from(new Map(data.map(item => [item.value, item])).values());
    }, [projectDropdownListData, memoizedSelectedProjects]);

    useEffect(() => {
        if (memoizedSelectedProjects?.length > 0) {
            methods.setValue(name, memoizedSelectedProjects);
        }
    }, [memoizedSelectedProjects]);

    return (
        <>
            <Select
                className={className}
                label={label}
                name={name}
                type={'dropdown'}
                options={memoizedProjectList}
                isLoading={isProjectDropDownListLoading}
                onInputChange={(event) => {
                    if (event.length > 0) {
                        let p = { query: event }
                        if (org_check_required) p.org_id = org_id;
                        debouncedProjectSearchSearch(p)
                    }
                }}
                onChange={(event) => {
                    onChange(event)
                }}
                required={required}
                isMulti={true}
                readOnly={readOnly}
            />
        </>
    )
}